import { motion } from 'motion/react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { Button } from './Button';
import { ArrowRight, Zap, Users, Calendar } from 'lucide-react';
import { Header } from './Header';
import { isFinnishPage } from '../../lib/blogPaths';
import { getWpImageSrc } from '../../lib/wpMedia';
import { HeroHomeVisual } from './HeroHomeVisual';

// Logos in order; all logos are white by default (white on transparent/black). Use /logos/{slug}.png.
const clientLogos = [
  { name: 'Mehiläinen', slug: 'mehilainen', width: 140 },
  { name: 'Helsinki', slug: 'helsinki', width: 110 },
  { name: 'Elisa', slug: 'elisa', width: 110 },
  { name: 'Visma', slug: 'visma', width: 90 },
  { name: 'Smartum', slug: 'smartum', width: 140 },
  { name: 'VARHA', slug: 'varha', width: 65 },
  { name: 'Twoday', slug: 'twoday', width: 130 },
  { name: 'Lyyti', slug: 'lyyti', width: 130 },
];

const heroStatsEn = [
  { icon: Zap, label: '100% AI-powered' },
  { icon: Users, label: '30+ Employees' },
  { icon: Calendar, label: 'Since 2018' },
];
const heroStatsFi = [
  { icon: Zap, label: '100% AI-natiivi tiimi' },
  { icon: Users, label: '30+ työntekijää' },
  { icon: Calendar, label: 'Vuodesta 2018' },
];

// Split title into words for stagger animation (home only)
const HOME_TITLE_WORDS = [
  'Your', 'trusted', 'partner', 'in', 'software', 'and', 'digital', 'development.',
];

/** Scroll target id for primary CTA when primaryHref is null (scroll-in-page). */
const HERO_SCROLL_TARGET_BY_VARIANT = {
  careers: 'open-positions',
  work: 'final-cta',
  services: 'final-cta',
  softwareDevelopment: 'final-cta',
  uiUxDesign: 'final-cta',
};
const DEFAULT_SCROLL_TARGET = 'contact-section';

const HERO_BASE = {
  showHeader: false,
  sectionClass: 'relative overflow-hidden bg-[#0f172a]',
  iconClass: 'w-5 h-5 text-highlight shrink-0',
  titleStyle: { color: '#ffffff' },
  descriptionStyle: { color: '#e2e8f0' },
  secondaryVariant: 'secondaryLight',
  showClientLogos: false,
  showStats: false,
};

const VARIANTS = {
  home: {
    ...HERO_BASE,
    sectionClass: 'relative overflow-hidden bg-[#0f172a] min-h-[500px] md:min-h-[600px]',
    title: 'Your trusted partner in software and digital development.',
    titleWords: HOME_TITLE_WORDS,
    description: 'We integrate seamlessly with your team to deliver exceptional results—powered by AI-assisted development for faster delivery.',
    statsClass: 'inline-flex items-center gap-2.5 py-2.5 px-5 rounded-full bg-white/10 text-base font-semibold text-white whitespace-nowrap w-full md:w-auto justify-center md:justify-start',
    descriptionClass: 'hero-description text-base md:text-[20px] font-normal leading-normal md:leading-relaxed mb-6 md:mb-8 max-w-full',
    titleClass: 'heading-1',
    descriptionStyle: { color: '#f1f5f9' },
    gridClass: 'grid grid-cols-1 gap-16 items-center mb-16',
    contentOrder: '',
    imageOrder: 'hidden',
    imageSrc: '/hero-frontpage.png',
    imageAlt: 'Identio team collaborating on software development',
    imageWrapperClass: '',
    primaryCta: 'Talk to Our Team',
    primaryHref: '/contact',
    secondaryCta: 'View Our Work',
    secondaryHref: '/work',
    showClientLogos: true,
    showStats: true,
    innerClass: 'max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-20',
  },
  services: {
    ...HERO_BASE,
    title: 'AI-Enhanced Development & Design for Enterprise Teams',
    description: 'We integrate seamlessly with your team to deliver exceptional results—powered by AI-assisted development for faster delivery.',
    statsClass: 'inline-flex items-center gap-2.5 py-2.5 px-5 rounded-full bg-white/10 text-base font-semibold text-white whitespace-nowrap w-full md:w-auto justify-center md:justify-start',
    iconClass: 'w-5 h-5 text-highlight shrink-0',
    descriptionClass: 'text-base md:text-lg text-gray-300 mt-6 mb-8 max-w-xl',
    titleClass: 'heading-1 text-4xl md:text-5xl lg:text-[56px] text-white',
    titleStyle: { color: '#ffffff' },
    descriptionStyle: { color: '#e2e8f0' },
    gridClass: 'grid grid-cols-1 lg:grid-cols-[1fr_0.9fr] gap-12 lg:gap-16 items-center',
    contentOrder: 'order-2 lg:order-1',
    imageOrder: 'relative order-1 lg:order-2 w-full mb-8 lg:mb-0',
    imageSrc: '/hero-image.png',
    imageAlt: 'Identio team member at work with laptops',
    imageWrapperClass: 'relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]',
    primaryCta: 'Talk to Our Team',
    secondaryCta: 'View Our Work',
    secondaryHref: '/work',
    secondaryVariant: 'secondaryLight',
    showClientLogos: false,
    showStats: false,
    innerClass: 'max-w-7xl mx-auto px-6 pt-8 pb-12 md:py-24 lg:py-28',
  },
  careers: {
    showHeader: false,
    sectionClass: 'relative overflow-hidden bg-[#0f172a]',
    title: "Don't compromise your colours. Join the team.",
    description: 'Identio is a technological company specialised in consulting. With us, you design and develop digital services—and grow as yourself.',
    statsClass: 'inline-flex items-center gap-2.5 py-2.5 px-5 rounded-full bg-white/10 text-base font-semibold text-white whitespace-nowrap w-full md:w-auto justify-center md:justify-start',
    iconClass: 'w-5 h-5 text-highlight shrink-0',
    descriptionClass: 'text-base md:text-lg text-gray-300 mt-6 mb-8 max-w-xl',
    titleClass: 'heading-1 text-4xl md:text-5xl lg:text-[52px] text-white',
    titleStyle: { color: '#ffffff' },
    descriptionStyle: { color: '#e2e8f0' },
    gridClass: 'grid grid-cols-1 lg:grid-cols-[1fr_0.9fr] gap-12 lg:gap-16 items-center',
    contentOrder: 'order-2 lg:order-1',
    imageOrder: 'relative order-1 lg:order-2 w-full mb-8 lg:mb-0',
    imageSrc: '/careers/hero-colours.png',
    imageAlt: 'Vibrant colours and energy—bring your true colours to the team',
    imageWrapperClass: 'relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]',
    primaryCta: 'See open positions',
    secondaryCta: 'Read about ownership',
    secondaryHref: 'https://www.identio.fi/blog/why-is-identio-owned-by-its-employees',
    secondaryVariant: 'secondaryLight',
    showStats: false,
    innerClass: 'max-w-7xl mx-auto px-6 pt-8 pb-12 md:py-24 lg:py-28',
  },
  work: {
    ...HERO_BASE,
    title: 'Meaningful success stories and top-level technology.',
    description:
      'Identio is a technology company that helps future-oriented companies utilise new and sustainable technologies and build a more workable digital future. We utilise the agile methods of modern software development in our work. We will provide professionals to support your team throughout the lifecycle of your software project.',
    statsClass: '',
    descriptionClass: 'text-base md:text-lg text-gray-300 mt-6 mb-8 max-w-3xl',
    titleClass: 'heading-1 text-4xl md:text-5xl lg:text-[52px] text-white',
    gridClass: 'grid grid-cols-1 gap-12 lg:gap-16 items-center mb-0',
    contentOrder: '',
    imageOrder: 'hidden',
    imageSrc: '',
    imageAlt: '',
    imageWrapperClass: '',
    primaryCta: 'Contact us',
    secondaryCta: 'Our services',
    secondaryHref: '/services',
    innerClass: 'max-w-7xl mx-auto px-6 pt-8 pb-12 md:py-24 lg:py-28',
  },
  team: {
    ...HERO_BASE,
    title: 'Team that combines experience and curiosity.',
    description:
      'We are professionals in the IT field, UI/UX designers and marketing specialists. We are also active hobbyists and love to relax on the couch. We all have one thing in common: we enjoy the possibility to develop more usable technology than before, and what is more, we get to do it around great people.',
    statsClass: '',
    descriptionClass: 'text-base md:text-lg text-gray-300 mt-6 mb-8 max-w-xl',
    titleClass: 'heading-1 text-4xl md:text-5xl lg:text-[52px] text-white',
    gridClass: 'grid grid-cols-1 lg:grid-cols-[1fr_0.9fr] gap-12 lg:gap-16 items-center mb-0',
    contentOrder: 'order-2 lg:order-1',
    imageOrder: 'relative order-1 lg:order-2 w-full mb-8 lg:mb-0',
    imageSrc: '/team/hero.png',
    imageAlt: 'Identio team on the water at sunset',
    imageWrapperClass: 'relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]',
    primaryCta: 'See open positions',
    primaryHref: '/careers#open-positions',
    secondaryCta: null,
    secondaryHref: null,
    innerClass: 'max-w-7xl mx-auto px-6 pt-8 pb-12 md:py-24 lg:py-28',
  },
  contact: {
    ...HERO_BASE,
    title: 'Are you considering cooperation, or do you just want to chat?',
    description: 'Reach out to our team—we’re happy to talk about your project or answer any questions.',
    statsClass: '',
    iconClass: 'w-5 h-5 text-highlight shrink-0',
    descriptionClass: 'text-base md:text-lg text-gray-300 mt-6 mb-8 max-w-2xl',
    titleClass: 'heading-1 text-4xl md:text-5xl lg:text-[52px] text-white',
    titleStyle: { color: '#ffffff' },
    descriptionStyle: { color: '#e2e8f0' },
    gridClass: 'grid grid-cols-1 gap-12 lg:gap-16 items-center mb-0',
    contentOrder: '',
    imageOrder: 'hidden',
    imageSrc: '',
    imageAlt: '',
    imageWrapperClass: '',
    primaryCta: null,
    primaryHref: null,
    secondaryCta: null,
    secondaryHref: null,
    secondaryVariant: 'secondaryLight',
    showClientLogos: false,
    showStats: false,
    innerClass: 'max-w-7xl mx-auto px-6 pt-8 pb-12 md:py-24 lg:py-28',
  },
  softwareDevelopment: {
    showHeader: false,
    sectionClass: 'relative overflow-hidden bg-[#0f172a]',
    showStats: false,
    title: 'Level up your business with software development.',
    description:
      'Quality and industry best practices. We solve challenges that are critical to your success both now and in the future. Our services range from modernizing legacy systems to developing entirely new ones, ensuring scalability and resilience. We are committed to delivering cost-effective, modern, and secure solutions, allowing you to focus on your core business.',
    statsClass: '',
    descriptionClass: 'text-base md:text-lg text-gray-300 mt-6 mb-8 max-w-xl',
    titleClass: 'heading-1 text-4xl md:text-5xl lg:text-[52px] text-white',
    gridClass: 'grid grid-cols-1 lg:grid-cols-[1fr_0.9fr] gap-12 lg:gap-16 items-center mb-0',
    contentOrder: 'order-2 lg:order-1',
    imageOrder: 'relative order-1 lg:order-2 w-full mb-8 lg:mb-0',
    imageSrc: '/services/hero.png',
    imageAlt: 'Strategic thinking and problem-solving—chessboard representing software architecture and planning',
    imageWrapperClass: 'relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]',
    primaryCta: 'Contact us',
    primaryHref: null,
    secondaryCta: null,
    secondaryHref: null,
    innerClass: 'max-w-7xl mx-auto px-6 pt-8 pb-12 md:py-24 lg:py-28',
  },
  uiUxDesign: {
    ...HERO_BASE,
    title: 'UX design engages users and creates high-level digital experiences.',
    description:
      'Comprehensive design skills. Our UX designers work as part of your existing development team or assist in the development of a new web or mobile application. Through UX design, we ensure that the application meets the needs of the users. By focusing on user-friendliness, we guarantee user engagement and the achievement of your business goals.',
    statsClass: '',
    descriptionClass: 'text-base md:text-lg text-gray-300 mt-6 mb-8 max-w-xl',
    titleClass: 'heading-1 text-4xl md:text-5xl lg:text-[52px] text-white',
    gridClass: 'grid grid-cols-1 lg:grid-cols-[1fr_0.9fr] gap-12 lg:gap-16 items-center mb-0',
    contentOrder: 'order-2 lg:order-1',
    imageOrder: 'relative order-1 lg:order-2 w-full mb-8 lg:mb-0',
    imageSrc: '/services/ui-ux-hero.png',
    imageAlt: 'UX designer working on digital experiences',
    imageWrapperClass: 'relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]',
    primaryCta: 'Contact us',
    primaryHref: null,
    secondaryCta: null,
    secondaryHref: null,
    innerClass: 'max-w-7xl mx-auto px-6 pt-8 pb-12 md:py-24 lg:py-28',
  },
};

/** Finnish overrides for services variants when pathname is /palvelut or under /palvelut/. */
const VARIANTS_FI = {
  work: {
    primaryCta: 'Ota yhteyttä',
    secondaryCta: 'Palvelumme',
    secondaryHref: '/palvelut',
  },
  services: {
    title: 'Korkeampaa tuottavuutta AI-tehostetulla ohjelmistokehityksellä ja UX-suunnittelulla',
    description: 'Sitoutuneita osaajia sovelluskehitykseen. Asiantuntijamme yhdistävät ketterät toimintatavat ja AI-työkalut maksimoidakseen tuottavuuden jokaisessa vaiheessa.',
    primaryCta: 'Ota yhteyttä',
    secondaryCta: 'Asiakastarinat',
    secondaryHref: '/tyomme',
    imageAlt: 'Identio-työntekijä työskentelemässä kannettavien äärellä',
  },
  softwareDevelopment: {
    title: 'Ohjelmistokehityksen kautta viet liiketoimintasi uudelle tasolle.',
    description: 'Laatua ja alan parhaita käytäntöjä. Ratkaisemme haasteet, jotka ovat kriittisiä menestyksenne kannalta nyt ja tulevaisuudessa. Tarjoamme palvelua vanhan modernisoinnista täysin uusien järjestelmien kehittämiseen.',
    primaryCta: 'Ota yhteyttä',
    imageAlt: 'Strateginen ajattelu ja ongelmanratkaisu—shakkilauta edustaa ohjelmistoarkkitehtuuria ja suunnittelua',
  },
  uiUxDesign: {
    title: 'UX-suunnittelulla sitoutuneita käyttäjiä ja korkean tason digitaalisia kokemuksia.',
    description: 'Kokonaisvaltaista design-osaamista. UX-suunnittelijamme työskentelevät osana kehitystiimiänne tai ovat apuna uuden verkko- tai mobiilisovelluksen kehityksessä. Varmistamme UX-suunnittelun kautta, että sovellus täyttää käyttäjien tarpeet.',
    primaryCta: 'Ota yhteyttä',
    imageAlt: 'UX-suunnittelija työskentelemässä digitaalisissa kokemuksissa',
  },
};

const DEFAULT_LOGO_WIDTH = 120;

export function HeroSection({
  variant = 'home',
  logos: logosProp,
  title: titleOverride,
  description: descriptionOverride,
  primaryCta: primaryCtaOverride,
  primaryHref: primaryHrefOverride,
  secondaryCta: secondaryCtaOverride,
  secondaryHref: secondaryHrefOverride,
}) {
  const router = useRouter();
  const isFinnish = variant === 'home' && isFinnishPage(router?.pathname);
  const isServicesFinnish =
    (variant === 'services' || variant === 'softwareDevelopment' || variant === 'uiUxDesign') &&
    isFinnishPage(router?.pathname);
  const heroStats = isFinnish ? heroStatsFi : heroStatsEn;
  let v = VARIANTS[variant] ?? VARIANTS.home;
  const useFinnishVariant =
    VARIANTS_FI[variant] &&
    (isServicesFinnish || (variant === 'work' && isFinnishPage(router?.pathname)));
  if (useFinnishVariant) {
    v = { ...v, ...VARIANTS_FI[variant] };
  }
  const title = titleOverride ?? v.title;
  const description = descriptionOverride ?? v.description;
  const primaryCta = primaryCtaOverride ?? v.primaryCta;
  const primaryHref = primaryHrefOverride ?? v.primaryHref;
  const secondaryCta = secondaryCtaOverride ?? v.secondaryCta;
  const secondaryHref = secondaryHrefOverride ?? v.secondaryHref;
  // Home: use up to 8 logos from WP when provided (ascending by date); else fallback to static list
  const displayLogos =
    variant === 'home' && logosProp?.length > 0
      ? logosProp.slice(0, 8).map((l) => ({
          name: l.name ?? '',
          slug: l.slug ?? '',
          logoUrl: l.logoUrl ?? null,
          width: l.width ?? DEFAULT_LOGO_WIDTH,
        }))
      : clientLogos.map((c) => ({ ...c, logoUrl: null }));
  const handlePrimaryCta = () => {
    const id = HERO_SCROLL_TARGET_BY_VARIANT[variant] ?? DEFAULT_SCROLL_TARGET;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const isHome = variant === 'home';
  const titleWords = titleOverride ? null : (v.titleWords ?? null);

  const heroImageSrc = v.imageSrc ? (getWpImageSrc(v.imageSrc) || v.imageSrc) : null;

  return (
    <>
      {heroImageSrc && (
        <Head>
          <link rel="preload" href={heroImageSrc} as="image" />
        </Head>
      )}
      {v.showHeader && <Header />}
      <section className={v.sectionClass} aria-label="Hero">
        {/* Home: animated shapes (no image); modern, bold, clean */}
        {isHome && (
          <HeroHomeVisual />
        )}
        {/* Animated background pattern (home hero) */}
        {isHome && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]" aria-hidden>
            <div className="hero-pattern absolute inset-0 opacity-[0.04]" />
          </div>
        )}
        <div className={`${v.innerClass} relative z-10 min-w-0`}>
          <div className={`${v.gridClass} min-w-0`}>
            <div className={`${v.contentOrder} min-w-0 ${v.imageOrder === 'hidden' ? 'max-w-3xl mx-auto' : ''}`}>
              <h1 className={isHome ? 'hero-headline text-white' : v.titleClass} style={v.titleStyle}>
                {titleWords ? (
                  titleWords.map((word, i) => (
                    <motion.span
                      key={`${word}-${i}`}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.02 + i * 0.03 }}
                      className="inline-block mr-[0.25em]"
                    >
                      {word}
                    </motion.span>
                  ))
                ) : (
                  <motion.span
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0 }}
                  >
                    {title}
                  </motion.span>
                )}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: variant === 'home' ? 0.15 : 0.1 }}
                className={v.descriptionClass}
                style={v.descriptionStyle}
              >
                {description}
              </motion.p>

              {v.showStats !== false && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: variant === 'home' ? 0.25 : 0.2 }}
                  className="flex flex-wrap gap-4 mb-8 md:flex-row flex-col"
                >
                  {heroStats.map(({ icon: Icon, label }) => (
                    <span key={label} className={v.statsClass}>
                      <Icon className={v.iconClass} aria-hidden />
                      {label}
                    </span>
                  ))}
                </motion.div>
              )}

              {primaryCta && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: variant === 'home' ? 0.35 : 0.3 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                {primaryHref ? (
                  <Button href={primaryHref}>
                    {primaryCta}
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                ) : (
                  <Button onClick={handlePrimaryCta}>
                    {primaryCta}
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                )}
                {(secondaryCta && secondaryHref) && (
                  <Button href={secondaryHref} variant={v.secondaryVariant}>
                    {secondaryCta}
                  </Button>
                )}
              </motion.div>
              )}
              {v.blogLinkText && v.blogLinkHref && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="mt-6"
                >
                  <Link
                    href={v.blogLinkHref}
                    className="inline-flex items-center gap-2 text-highlight hover:underline font-medium text-[15px] hover:gap-3 transition-all group"
                  >
                    {v.blogLinkText}
                    <ArrowRight className="w-4 h-4 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </motion.div>
              )}
            </div>

            {v.imageOrder !== 'hidden' && (
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: variant === 'home' ? 0.8 : 0.6, delay: variant === 'home' ? 0.5 : 0.2 }}
              className={`${v.imageOrder} hidden md:block`}
            >
              <div className={`${v.imageWrapperClass} relative ${v.imageSrc ? 'h-[260px] md:h-[440px] lg:h-[480px]' : ''}`}>
                {v.imageSrc && (
                <Image
                  src={getWpImageSrc(v.imageSrc) || v.imageSrc}
                  alt={v.imageAlt}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  priority
                  className="object-cover"
                  unoptimized
                />
                )}
                {/* Gradient + soft blur edge so text area contrasts; stronger on home */}
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                {isHome && (
                  <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_80px_40px_rgba(15,23,42,0.4)]" />
                )}
              </div>
            </motion.div>
            )}
          </div>

          {v.showClientLogos && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="border-t border-white/10 pt-8"
            >
              <p className="text-sm text-[#94a3b8] mb-6 text-center">Trusted by</p>
              <div className="client-logos grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 justify-center justify-items-center gap-4 md:gap-6 mb-8 px-4 md:px-0 max-w-5xl mx-auto min-w-0 overflow-hidden">
                {displayLogos.map((clientLogo, index) => (
                  <motion.div
                    key={clientLogo.slug || clientLogo.name || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 1 + index * 0.1 }}
                    className="h-10 md:h-12 flex items-center justify-center min-w-0"
                    style={{ width: clientLogo.width ?? DEFAULT_LOGO_WIDTH, maxWidth: '140px' }}
                  >
                    <div className="h-full w-full flex items-center justify-center opacity-70 hover:opacity-100 transition-[transform,opacity] duration-500 ease-out hover:scale-[1.04]">
                      {clientLogo.logoUrl ? (
                        <img
                          src={getWpImageSrc(clientLogo.logoUrl) || clientLogo.logoUrl}
                          alt={clientLogo.name || 'Client logo'}
                          width={96}
                          height={48}
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <img
                          src={`/logos/${clientLogo.slug}.png`}
                          alt={clientLogo.name}
                          width={96}
                          height={48}
                          className="max-w-full max-h-full object-contain brightness-0 invert"
                        />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
}

HeroSection.propTypes = {
  variant: PropTypes.string,
  logos: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      logoUrl: PropTypes.string,
      width: PropTypes.number,
    })
  ),
  title: PropTypes.string,
  description: PropTypes.string,
  primaryCta: PropTypes.string,
  primaryHref: PropTypes.string,
};
